import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { usePmoScaleTailProjects } from '../../../hooks/usePmoFlow.js';
import { BACKLOG_BASE } from '../project/index.js';
import type { CSSProperties } from 'react';
import type {
  PmoScaleExcludeMode,
  PmoScaleScopeStat,
  PmoScaleTailPercentile,
  PmoScaleTailProject,
} from '../../../types/pmo-flow.js';

/** チャートの高さ（Tailwind の h-72 と一致させる）。 @graph-connects none */
const CHART_HEIGHT_CLASS = 'h-72';

/** 分布曲線の色。カード側の作業系（暖色）と揃える。 @graph-connects none */
const CURVE_COLOR = '#E8743B';

/**
 * 明細のしきい値として選べる分位。
 *
 * p90 だけだと母数の小さい帯で数件しか並ばず、傾向が読めない。p75 まで下ろすと
 * 「伸びた側 1/4」が見えるので、2 段階を切り替えられるようにする
 * @graph-connects none
 */
const TAIL_PERCENTILE_OPTIONS: readonly PmoScaleTailPercentile[] = [75, 90] as const;

/** 曲線上に縦線を引く分位（カードが出している 2 点と揃える）。 @graph-connects none */
const MARKER_PERCENTILES = [50, 90] as const;

/**
 * 曲線に描く最大の分位。**p100（最長の1件）は曲線に載せない。**
 *
 * p100 はたった 1 件で決まるので、その 1 件が飛び抜けていると縦軸を独占する。
 * 実データ（直近6ヶ月まとめ・1〜50pt 帯）でも p99 = 139 営業日 に対し p100 = 362 営業日 で、
 * 縦軸に入れると他の 100 点が下半分へ潰れて曲線の形が読めなくなる。
 *
 * 曲線からは外すが**隠さない**。最長の 1 件は下の尾側明細の先頭行（営業日の降順）に
 * 案件キーつきで並ぶので、値も中身も追える。
 * @graph-connects none
 */
const CHART_MAX_PERCENTILE = 99;

export interface ScaleLeadTimeDistributionModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 分布を出す対象（規模帯 or 全体）。未選択なら null */
  scope: PmoScaleScopeStat | null;
  /** カードが表示しているリリース完了月。明細もこの月で引かないと分位が別物になる */
  month: string;
  /** カードが適用している除外モード。曲線と明細の母集団を揃えるために必ず同じ値を渡す */
  excludeMode: PmoScaleExcludeMode;
}

/**
 * 分位配列をチャート用の点列へ変換する。添字がそのまま分位なので index を x に使う。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 規模帯の分位配列を曲線用データ点（p0〜p99。外れ値1件で決まるp100は除く）へ変換する
 * @graph-connects none
 */
export function toDistributionPoints(
  distribution: readonly number[],
): { percentile: number; days: number }[] {
  return distribution
    .map((days, percentile) => ({ percentile, days }))
    .filter((point) => point.percentile <= CHART_MAX_PERCENTILE);
}

/**
 * 尾側案件 1 行。営業日はインラインの横棒でも出し、行同士の伸び方を目で比べられるようにする。
 * 棒の基準はこの一覧の最長案件（＝ p100 側）で、他の対象とは比べない。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 規模帯の尾側案件1行（案件キー・件名・担当・実績pt・開始日・リリース完了日・営業日と相対バー）を描画する
 * @graph-connects none
 */
function TailProjectRow({ project, maxDays }: { project: PmoScaleTailProject; maxDays: number }) {
  const { t } = useTranslation();
  const barStyle: CSSProperties = {
    backgroundColor: CURVE_COLOR,
    width: `${String(maxDays > 0 ? (project.days / maxDays) * 100 : 0)}%`,
  };

  return (
    <tr className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]">
      <td className="whitespace-nowrap px-3 py-2">
        <a
          href={`${BACKLOG_BASE}${project.issueKey}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-accent-primary)] hover:underline"
        >
          {project.issueKey}
        </a>
      </td>
      <td className="px-3 py-2 text-[var(--color-text-primary)]">{project.summary}</td>
      <td className="whitespace-nowrap px-3 py-2 text-[var(--color-text-secondary)]">
        {project.responsibleTeam ?? '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-[var(--color-text-secondary)]">
        {project.devLeader ?? '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text-secondary)]">
        {project.actualPoint}
      </td>
      <td className="whitespace-nowrap px-3 py-2 font-mono tabular-nums text-[var(--color-text-muted)]">
        {project.startDate ?? '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 font-mono tabular-nums text-[var(--color-text-muted)]">
        {project.releasedDate ?? '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-2">
        <span className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold tabular-nums text-[var(--color-text-primary)]">
            {project.days}
            <span className="ml-0.5 text-xs font-normal text-[var(--color-text-muted)]">
              {t('kpi.pmoFlow.unit.daysSuffix')}
            </span>
          </span>
          <span className="h-1.5 w-24 overflow-hidden rounded-full bg-[var(--color-bg-hover)]">
            <span
              className="block h-full rounded-full"
              style={barStyle}
              data-testid="pmo-scale-tail-bar"
            />
          </span>
        </span>
      </td>
    </tr>
  );
}

/**
 * 対象 1 つ分（規模帯 or 全体）のリードタイム分布（p0〜p100）を出すモーダル。
 *
 * カードは p50 / p90 の 2 点しか出さないが、それだけでは「p90 の先がどこまで伸びるか」も
 * 「短い側がどれだけ詰まっているか」も読めない。曲線の立ち上がり方そのものが
 * 見積の当てやすさを表すので、分位を横軸にした累積曲線で分布の形を見せる。
 *
 * 横軸は分位（0〜100）で件数ではない。ヒストグラムにすると階級幅の取り方で形が変わり、
 * 母数が数十件の帯では階級の選び方だけで印象が動いてしまうため、階級を持たない
 * 分位曲線にしている。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO案件規模別リードタイムのドリルダウン。選択した対象（規模帯 or 全体）の営業日の分位曲線と、指定分位（p75/p90）以上の案件明細を表示する
 * 分位だけでは「どこまで伸びうるか」しか分からず、何を直せばいいかは伸びた案件そのものを
 * 見ないと決まらない。そのため尾側（p75 / p90 以上）は明細でも並べる。しきい値はサマリーから
 * 渡さず明細 API 側の計算値を使うので、画面のしきい値と一覧の中身は必ず一致する。
 *
 * @graph-connects toDistributionPoints [calls] 分位配列のチャート用点列化
 * @graph-connects usePmoScaleTailProjects [calls] 尾側案件明細の取得フック呼び出し
 * @graph-connects TailProjectRow [calls] 尾側案件1行の描画
 * @graph-connects Modal [calls] モーダル描画
 * @graph-connects AreaChart [calls] 分位曲線描画
 */
export function ScaleLeadTimeDistributionModal({
  open,
  onClose,
  scope,
  month,
  excludeMode,
}: ScaleLeadTimeDistributionModalProps) {
  const { t } = useTranslation();
  const [tailPercentile, setTailPercentile] = useState<PmoScaleTailPercentile>(90);

  const points = scope ? toDistributionPoints(scope.distribution) : [];
  const tail = usePmoScaleTailProjects(
    scope?.key ?? null,
    month,
    tailPercentile,
    excludeMode,
    open,
  );
  const tailProjects = tail.data?.projects ?? [];
  // 棒の基準はこの一覧の最長案件。先頭が最長（API が営業日の降順で返す）
  const maxTailDays = tailProjects[0]?.days ?? 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={
        scope
          ? t('kpi.pmoFlow.scaleLeadTime.distribution.title', {
              band: t(`kpi.pmoFlow.scaleLeadTime.band.${scope.key}`),
            })
          : ''
      }
      description={
        scope
          ? t('kpi.pmoFlow.scaleLeadTime.distribution.description', { count: scope.count })
          : undefined
      }
    >
      {points.length === 0 ? (
        <div className="py-12 text-center text-[var(--color-text-muted)]">
          {t('kpi.pmoFlow.scaleLeadTime.noSample')}
        </div>
      ) : (
        <>
          <div className={CHART_HEIGHT_CLASS} data-testid="pmo-scale-distribution-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 8, right: 16, bottom: 24, left: 8 }}>
                <defs>
                  <linearGradient id="pmoScaleDistributionFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CURVE_COLOR} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CURVE_COLOR} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="percentile"
                  type="number"
                  domain={[0, CHART_MAX_PERCENTILE]}
                  ticks={[0, 10, 25, 50, 75, 90, CHART_MAX_PERCENTILE]}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  label={{
                    value: t('kpi.pmoFlow.scaleLeadTime.distribution.axisPercentile'),
                    position: 'insideBottom',
                    offset: -12,
                    style: { fontSize: 11, fill: 'var(--color-text-muted)' },
                  }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  label={{
                    value: t('kpi.pmoFlow.scaleLeadTime.distribution.axisDays'),
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 11, fill: 'var(--color-text-muted)' },
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(value) =>
                    t('kpi.pmoFlow.scaleLeadTime.distribution.tooltipLabel', {
                      percentile: String(value),
                    })
                  }
                  formatter={(value) => [
                    `${String(value)}${t('kpi.pmoFlow.unit.daysSuffix')}`,
                    t('kpi.pmoFlow.scaleLeadTime.distribution.axisDays'),
                  ]}
                />
                {MARKER_PERCENTILES.map((percentile) => (
                  <ReferenceLine
                    key={percentile}
                    x={percentile}
                    stroke="var(--color-text-muted)"
                    strokeDasharray="4 4"
                    label={{
                      value: `p${String(percentile)}`,
                      position: 'top',
                      style: { fontSize: 10, fill: 'var(--color-text-muted)' },
                    }}
                  />
                ))}
                <Area
                  type="monotone"
                  dataKey="days"
                  stroke={CURVE_COLOR}
                  strokeWidth={2}
                  fill="url(#pmoScaleDistributionFill)"
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <section className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {t('kpi.pmoFlow.scaleLeadTime.tail.title', { percentile: tailPercentile })}
              </h3>
              {/* しきい値の切り替え。2 択なのでセレクトを開かせず、その場で押せる形にする */}
              <span
                className="flex overflow-hidden rounded-lg border border-[var(--color-border)]"
                role="group"
                aria-label={t('kpi.pmoFlow.scaleLeadTime.tail.percentileLabel')}
              >
                {TAIL_PERCENTILE_OPTIONS.map((percentile) => (
                  <button
                    key={percentile}
                    type="button"
                    aria-pressed={percentile === tailPercentile}
                    onClick={() => {
                      setTailPercentile(percentile);
                    }}
                    className={`cursor-pointer px-3 py-1 text-xs font-medium transition-colors ${
                      percentile === tailPercentile
                        ? 'bg-[var(--color-accent-primary)] text-white'
                        : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                    }`}
                    data-testid={`pmo-scale-tail-percentile-${String(percentile)}`}
                  >
                    {t('kpi.pmoFlow.scaleLeadTime.tail.percentileOption', { percentile })}
                  </button>
                ))}
              </span>
            </div>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              {tail.data?.thresholdDays === null || tail.data === undefined
                ? t('kpi.pmoFlow.scaleLeadTime.tail.descriptionPending', {
                    percentile: tailPercentile,
                  })
                : t('kpi.pmoFlow.scaleLeadTime.tail.description', {
                    threshold: tail.data.thresholdDays,
                    count: tailProjects.length,
                  })}
            </p>

            {tail.isLoading ? (
              <div className="mt-3 h-24 animate-pulse rounded-lg bg-[var(--color-bg-elevated)]" />
            ) : tailProjects.length === 0 ? (
              <p className="mt-3 py-6 text-center text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.scaleLeadTime.tail.noItems', { percentile: tailPercentile })}
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnIssueKey')}
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnSummary')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnTeam')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnDevLeader')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-right font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnActualPoint')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnStartDate')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnReleasedDate')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-[var(--color-text-muted)]">
                        {t('kpi.pmoFlow.scaleLeadTime.tail.columnDays')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tailProjects.map((project) => (
                      <TailProjectRow
                        key={project.issueKey}
                        project={project}
                        maxDays={maxTailDays}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </Modal>
  );
}
